import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EventModal = ({ event, isNew, onClose, onSave, onDelete, canEdit }) => {
  // Função para validar e extrair dados do evento
  const extractEventData = (event) => {
    if (!event) return {};
    
    try {
      // Extrair data e hora do startTime
      let startDate = '';
      let startTime = '';
      if (event.startTime) {
        if (event.startTime.includes('T')) {
          // Formato ISO: "2025-08-27T09:00:00+00:00"
          const [datePart, timePart] = event.startTime.split('T');
          startDate = datePart;
          startTime = timePart.split('+')[0].substring(0, 5); // "09:00"
        } else if (event.startTime.includes(' ')) {
          // Formato simples: "2025-08-27 09:00"
          [startDate, startTime] = event.startTime.split(' ');
        }
      }

      // Extrair data e hora do endTime
      let endDate = '';
      let endTime = '';
      if (event.endTime) {
        if (event.endTime.includes('T')) {
          // Formato ISO: "2025-08-27T10:00:00+00:00"
          const [datePart, timePart] = event.endTime.split('T');
          endDate = datePart;
          endTime = timePart.split('+')[0].substring(0, 5); // "10:00"
        } else if (event.endTime.includes(' ')) {
          // Formato simples: "2025-08-27 10:00"
          [endDate, endTime] = event.endTime.split(' ');
        }
      }

      return {
        title: event.title || '',
        type: event.type || 'atendimento',
        startDate: startDate || new Date().toISOString().split('T')[0],
        startTime: startTime || '09:00',
        endDate: endDate || new Date().toISOString().split('T')[0],
        endTime: endTime || '10:00',
        description: event.description || '',
        visibility: event.visibility || 'public',
        attendees: event.attendees?.join(', ') || ''
      };
    } catch (error) {
      console.error('❌ Erro ao extrair dados do evento:', error);
      return {
        title: event.title || '',
        type: event.type || 'atendimento',
        startDate: new Date().toISOString().split('T')[0],
        startTime: '09:00',
        endDate: new Date().toISOString().split('T')[0],
        endTime: '10:00',
        description: event.description || '',
        visibility: event.visibility || 'public',
        attendees: event.attendees?.join(', ') || ''
      };
    }
  };

  const [formData, setFormData] = useState(extractEventData(event));
  const [timeErrors, setTimeErrors] = useState({});

  // Sincronizar formData quando event mudar
  useEffect(() => {
    console.log('🔍 EventModal: event mudou, atualizando formData:', event);
    const newFormData = extractEventData(event);
    console.log('🔍 EventModal: novo formData extraído:', newFormData);
    setFormData(newFormData);
  }, [event]);

  const eventTypeOptions = [
    { value: 'atendimento', label: 'Atendimento' },
    { value: 'disponivel', label: 'Disponível' },
    { value: 'bloqueado', label: 'Ocupado' },
    { value: 'evento', label: 'Evento' }
  ];

  const visibilityOptions = [
    { value: 'public', label: 'Público' },
    { value: 'private', label: 'Privado' },
    { value: 'restricted', label: 'Restrito' }
  ];

  // Validate time range
  const validateTimeRange = (startDate, startTime, endDate, endTime) => {
    if (!startDate || !startTime || !endDate || !endTime) {
      return { isValid: false, message: 'Todos os campos de data e horário são obrigatórios' };
    }
    
    const start = new Date(`${startDate} ${startTime}`);
    const end = new Date(`${endDate} ${endTime}`);
    
    if (end <= start) {
      return {
        isValid: false,
        message: 'Horário de término deve ser posterior ao horário de início'
      };
    }
    
    return { isValid: true, message: '' };
  };

  // Validate event type
  const validateEventType = (type) => {
    const validTypes = ['atendimento', 'disponivel', 'bloqueado', 'evento'];
    return validTypes.includes(type);
  };

  // Validate visibility
  const validateVisibility = (visibility) => {
    const validVisibilities = ['public', 'private', 'restricted'];
    return validVisibilities.includes(visibility);
  };

  // Calculate duration between start and end times
  const calculateDuration = () => {
    if (!formData.startDate || !formData.startTime || !formData.endDate || !formData.endTime) {
      return null;
    }

    try {
      const start = new Date(`${formData.startDate} ${formData.startTime}`);
      const end = new Date(`${formData.endDate} ${formData.endTime}`);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return null;
      }

      const durationMs = end.getTime() - start.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));
      
      if (durationMinutes < 0) return null;
      
      if (durationMinutes < 60) {
        return `${durationMinutes}min`;
      } else {
        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes % 60;
        if (minutes === 0) {
          return `${hours}h`;
        } else {
          return `${hours}h ${minutes}min`;
        }
      }
    } catch (error) {
      console.error('❌ Erro ao calcular duração:', error);
      return null;
    }
  };

  // Handle time/date changes with validation
  const handleTimeChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    // Validate time range when both dates and times are present
    if (newFormData.startDate && newFormData.startTime && newFormData.endDate && newFormData.endTime) {
      const validation = validateTimeRange(
        newFormData.startDate,
        newFormData.startTime,
        newFormData.endDate,
        newFormData.endTime
      );
      
      setTimeErrors(prev => ({
        ...prev,
        timeRange: validation.isValid ? '' : validation.message
      }));
    } else {
      // Clear error if not all fields are filled
      setTimeErrors(prev => ({ ...prev, timeRange: '' }));
    }
    
    setFormData(newFormData);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Final validation before submit
    const timeValidation = validateTimeRange(
      formData.startDate,
      formData.startTime,
      formData.endDate,
      formData.endTime
    );
    
    if (!timeValidation.isValid) {
      setTimeErrors(prev => ({ ...prev, timeRange: timeValidation.message }));
      return;
    }

    // Validate event type
    if (!validateEventType(formData.type)) {
      setTimeErrors(prev => ({ ...prev, timeRange: 'Tipo de evento inválido' }));
      return;
    }

    // Validate visibility
    if (!validateVisibility(formData.visibility)) {
      setTimeErrors(prev => ({ ...prev, timeRange: 'Visibilidade inválida' }));
      return;
    }
    
    const eventData = {
      ...event,
      ...formData,
      startTime: `${formData?.startDate} ${formData?.startTime}`,
      endTime: `${formData?.endDate} ${formData?.endTime}`,
      attendees: formData?.attendees?.split(',')?.map(email => email?.trim())?.filter(Boolean) || []
    };
    onSave?.(eventData);
  };

  const getEventTypeIcon = (type) => {
    const iconMap = {
      'atendimento': '📅',
      'disponivel': '🔓',
      'bloqueado': '🚫',
      'evento': '🎯'
    };
    return iconMap?.[type] || '📋';
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '';
    return new Date(dateTime)?.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-tier-3 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border event-modal">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{getEventTypeIcon(formData?.type)}</span>
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground">
                {isNew ? 'Novo Agendamento' : (canEdit ? 'Editar Evento' : 'Visualizar Evento')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isNew ? 'Crie um novo evento no seu calendário' : canEdit ?'Modifique os detalhes do evento' : 'Detalhes do evento'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Input
                  label="Título do Evento"
                  value={formData?.title}
                  onChange={(e) => setFormData(prev => ({...prev, title: e?.target?.value}))}
                  placeholder="Ex: Mentoria com João Silva"
                  required
                  disabled={!canEdit}
                />
              </div>

              <Select
                label="Tipo de Evento"
                value={formData?.type}
                onChange={(value) => setFormData(prev => ({...prev, type: value}))}
                options={eventTypeOptions}
                disabled={!canEdit}
              />

              <Select
                label="Visibilidade"
                value={formData?.visibility}
                onChange={(value) => setFormData(prev => ({...prev, visibility: value}))}
                options={visibilityOptions}
                disabled={!canEdit}
              />
            </div>

            {/* Date and Time */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Data e Horário</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground">Início</label>
                  <div className="flex space-x-2 mt-1">
                    <Input
                      type="date"
                      value={formData?.startDate}
                      onChange={(e) => handleTimeChange('startDate', e?.target?.value)}
                      required
                      disabled={!canEdit}
                    />
                    <Input
                      type="time"
                      value={formData?.startTime}
                      onChange={(e) => handleTimeChange('startTime', e?.target?.value)}
                      required
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground">Término</label>
                  <div className="flex space-x-2 mt-1">
                    <Input
                      type="date"
                      value={formData?.endDate}
                      onChange={(e) => handleTimeChange('endDate', e?.target?.value)}
                      required
                      disabled={!canEdit}
                    />
                    <Input
                      type="time"
                      value={formData?.endTime}
                      onChange={(e) => handleTimeChange('endTime', e?.target?.value)}
                      required
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>

              {/* Duration Display */}
              <div className="text-sm text-muted-foreground">
                <span>📊 Duração: </span>
                {(() => {
                  const start = new Date(`${formData.startDate} ${formData.startTime}`);
                  const end = new Date(`${formData.endDate} ${formData.endTime}`);
                  const durationMinutes = Math.max(0, (end?.getTime() - start?.getTime()) / (1000 * 60));
                  const hours = Math.floor(durationMinutes / 60);
                  const minutes = durationMinutes % 60;
                  
                  if (hours > 0) {
                    return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
                  }
                  return `${minutes}min`;
                })()}
              </div>

              {/* Time validation error */}
              {timeErrors.timeRange && (
                <div className="text-sm text-color-warning bg-destructive/10 p-3 rounded-md border border-destructive/20">
                  ⚠️ {timeErrors.timeRange}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Descrição</label>
              <textarea
                value={formData?.description}
                onChange={(e) => setFormData(prev => ({...prev, description: e?.target?.value}))}
                placeholder="Adicione detalhes sobre o evento..."
                rows={4}
                disabled={!canEdit}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              />
            </div>

            {/* Attendees */}
            {formData?.type === 'atendimento' && (
              <Input
                label="Participantes (emails separados por vírgula)"
                value={formData?.attendees}
                onChange={(e) => setFormData(prev => ({...prev, attendees: e?.target?.value}))}
                placeholder="email@exemplo.com, outro@exemplo.com"
                disabled={!canEdit}
              />
            )}

            {/* Current Event Info (for editing existing events) */}
            {!isNew && (
              <div className="bg-background/50 border border-border rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-2">Informações Atuais</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <span className="ml-2 font-medium">{event?.status}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Criado em:</span>
                    <span className="ml-2 font-medium">
                      {event?.createdAt ? formatDateTime(event?.createdAt) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border bg-background/50">
            {/* Delete Button - Only show for existing events that can be edited */}
            {canEdit && !isNew && onDelete && (
              <Button
                type="button"
                variant="destructive"
                iconName="Trash2"
                iconPosition="left"
                onClick={() => onDelete(event.id)}
              >
                Excluir
              </Button>
            )}
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
              >
                {canEdit ? 'Cancelar' : 'Fechar'}
              </Button>
              
              {canEdit && (
                <Button
                  type="submit"
                  variant="default"
                  iconName={isNew ? 'Plus' : 'Save'}
                  iconPosition="left"
                >
                  {isNew ? 'Criar Evento' : 'Salvar Alterações'}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;